import {
  Control,
  FieldError,
  UseFormRegisterReturn,
} from '@ltvco/refresh-lib/vendors';
import { memo } from 'react';

import { BaseSearchInput } from './BaseSearchInput';
import {
  composeHOC,
  withErrorHandling,
  withClearButton,
  withFloatingLabel,
} from './hoc';

interface SearchInputProps {
  /**
   * Class name for the input field
   */
  className?: string;
  /**
   * Registration object from react-hook-form
   */
  registerReturn: UseFormRegisterReturn;
  /**
   * Label for the input field that appears when focused or filled
   */
  label: string;
  /**
   * Placeholder text for the input field
   */
  placeholder: string;
  /**
   * Field error object from react-hook-form
   */
  error?: FieldError;
  /**
   * Function to clear errors for the field
   */
  // eslint-disable-next-line no-unused-vars
  clearErrors: (fieldName?: string) => void;
  /**
   * Function to call when the clear button is clicked
   */
  onClear: () => void;
  /**
   * Current value of the input field
   */
  value: string;
  /**
   * Name of the field for error handling
   */
  fieldName: string;
  /**
   * Control object from react-hook-form
   */
  control: Control;
  /**
   * Whether to enable autofocus on the input
   */
  autoFocus?: boolean;
  /**
   * Additional props to pass to the input component
   */
  inputProps?: Record<string, unknown>;
  /**
   * Additional props to pass to the input component
   */
  InputProps?: Record<string, unknown>;
  /**
   * Custom on change handler
   */
  customOnChange?: (
    // eslint-disable-next-line no-unused-vars
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    // eslint-disable-next-line no-unused-vars
    field: { onChange: (value: string) => void }
  ) => void;
  /**
   * Callback when input is focused
   */
  // eslint-disable-next-line no-unused-vars
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Callback when input loses focus
   */
  // eslint-disable-next-line no-unused-vars
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

// Create an enhanced search input by composing HOCs
const EnhancedSearchInput = composeHOC(
  withErrorHandling,
  withClearButton,
  withFloatingLabel
)(BaseSearchInput);

export const SearchInput = memo(
  ({
    className = '',
    registerReturn,
    label,
    placeholder,
    error,
    clearErrors,
    onClear,
    value,
    fieldName,
    control,
    autoFocus = false,
    inputProps = {},
    InputProps = {},
    customOnChange,
    onFocus,
    onBlur,
  }: SearchInputProps) => {
    const hasError = !!error;

    return (
      <EnhancedSearchInput
        className={`search-hub-variation-search-input ${className}`}
        // Base input props
        registerReturn={registerReturn}
        placeholder={placeholder}
        value={value}
        fieldName={fieldName}
        control={control}
        autoFocus={autoFocus}
        inputProps={inputProps}
        InputProps={InputProps}
        customOnChange={customOnChange}
        clearErrors={clearErrors}
        hasError={hasError}
        // Error handling props
        error={error}
        // Clear button props
        onClear={onClear}
        // Floating label props
        label={label}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  }
);
